import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Chat as ChatIcon } from "../icons";
import { LocalShipping as LocalShippingIcon } from "../icons";
import { doc, getDoc } from "firebase/firestore";
import { auth, db, functions } from "../firebase";
import { T } from "./Translate";
import PageHeader from "./PageHeader";
import PaypalButton from "../components/PaypalButton";
import DiscountPriceBreakdown from "../components/DiscountPriceBreakdown";
import { notifyUser } from "../utils/notifications";
import { getOfferFinalPrice, offerSellsWithPaypal } from "./homeUtils";
import { pageContentSx } from "../pageLayout";
import { brandColors } from "../theme";

// Checkout de una oferta con "Cobrar con PayPal" (ver publishOffer.js):
// mismo esquema que ProductCheckout (tiendas). El pago va directo al PayPal
// del vendedor, la venta queda en /offerSales y, ya pagado, el envío se
// acuerda con el vendedor por chat.
const OfferCheckout = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null); // "success" | "unsaved"

  useEffect(() => {
    let active = true;
    getDoc(doc(db, "offers", offerId))
      .then((snap) => active && setOffer(snap.exists() ? { id: snap.id, ...snap.data() } : null))
      .catch((e) => console.error("Error al obtener la oferta:", e))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [offerId]);

  const title = offer?.product || offer?.title;
  const finalPrice = offer ? getOfferFinalPrice(offer) : 0;
  const discount = Number(offer?.offer) || 0;
  const buyerUid = auth.currentUser?.uid;
  const isOwnOffer = offer?.uid === buyerUid;

  const handleApprove = async (details) => {
    try {
      // La venta la guarda recordPayPalPayment después de verificar el pago
      // con PayPal.
      await functions.httpsCallable("recordPayPalPayment")({ kind: "offerSale", targetId: offerId, orderId: details.id });
      notifyUser({
        userId: offer.uid,
        companyId: buyerUid,
        category: "messages",
        type: "message",
        message: `Vendiste "${title}". Acordá el envío con el comprador por chat.`,
        postId: offerId,
      });
      setResult("success");
    } catch (error) {
      // El cobro ya se hizo en PayPal: aunque no se haya guardado, el
      // comprador tiene que poder hablar con el vendedor.
      console.error("Error al registrar la venta de la oferta:", error);
      setResult("unsaved");
    }
  };

  const goToChat = () => navigate(`/chatbox/directo/${offer.uid}`);

  let content;
  if (loading) {
    content = (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  } else if (!offer || !offerSellsWithPaypal(offer)) {
    content = <Alert severity="error"><T>Esta oferta no se puede comprar por PayPal.</T></Alert>;
  } else {
    content = (
      <Paper sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 4, border: "1px solid", borderColor: "divider" }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            {offer.images?.[0] && (
              <Box
                component="img"
                src={offer.images[0]}
                alt={title}
                sx={{ width: 72, height: 72, objectFit: "cover", borderRadius: 2, flexShrink: 0 }}
              />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>{title}</Typography>
              {offer.name && (
                <Typography variant="body2" color="text.secondary">
                  <T>Vendido por</T> {offer.name}
                </Typography>
              )}
            </Box>
          </Stack>

          {discount > 0 ? (
            <DiscountPriceBreakdown
              originalPrice={Number(offer.price)}
              discountPercent={discount}
              finalPrice={finalPrice}
            />
          ) : (
            <Typography sx={{ fontWeight: 700 }}>
              <T>Precio:</T> ${finalPrice.toFixed(2)}
            </Typography>
          )}

          <Divider />

          {result ? (
            <>
              <Alert severity={result === "success" ? "success" : "warning"}>
                {result === "success" ? (
                  <T>¡Pago completado! Ya le avisamos al vendedor.</T>
                ) : (
                  <T>El pago se realizó, pero no se pudo guardar en tus compras. Escribile al vendedor con el comprobante de PayPal.</T>
                )}
              </Alert>
              <Paper
                variant="outlined"
                sx={{ p: 2.5, borderRadius: 3, borderColor: brandColors.divider, bgcolor: brandColors.backgroundDefault }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <LocalShippingIcon sx={{ color: brandColors.accent }} />
                  <Typography sx={{ fontWeight: 700 }}>
                    <T>Acordá el envío con el vendedor</T>
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <T>Escribile por chat para coordinar la dirección, la forma de envío o el retiro de tu compra.</T>
                </Typography>
                <Button fullWidth variant="contained" startIcon={<ChatIcon />} onClick={goToChat} sx={{ textTransform: "none" }}>
                  <T>Acordar envío por chat</T>
                </Button>
              </Paper>
              <Button onClick={() => navigate("/my-purchases")} sx={{ textTransform: "none" }}>
                <T>Ver mis compras</T>
              </Button>
            </>
          ) : isOwnOffer ? (
            <Alert severity="info"><T>Esta es tu oferta: no podés comprarla.</T></Alert>
          ) : (
            <>
              <Alert severity="info" icon={<LocalShippingIcon fontSize="inherit" />}>
                <T>El pago va directo al vendedor. Después de pagar vas a poder acordar el envío con él por chat.</T>
              </Alert>
              <PaypalButton
                amount={finalPrice}
                currency="USD"
                description={`Compra: ${title}`}
                clientId={process.env.REACT_APP_PAYPAL_CLIENT_ID}
                payeeEmail={offer.paypalEmail}
                metadata={{ customId: offerId }}
                onApprove={handleApprove}
              />
            </>
          )}
        </Stack>
      </Paper>
    );
  }

  return (
    <Container maxWidth={false} disableGutters sx={pageContentSx}>
      <PageHeader title={<T>Finalizar compra</T>} subtitle={title} />
      <Container maxWidth="sm" sx={{ mt: 4, mb: 12 }}>
        {content}
      </Container>
    </Container>
  );
};

export default OfferCheckout;
